import { Trans, useLingui } from '@lingui/react/macro';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components';
import classNames from 'classnames';

import type { SeverityLabel } from '@/utils/hooks/useRblSeverities';

export const SeverityTag = ({
  hideTooltip,
  isPatientPrintout,
  severity,
}: {
  hideTooltip?: boolean;
  isPatientPrintout?: boolean;
  severity?: SeverityLabel | 'unknown' | null;
}) => {
  const { t } = useLingui();

  if (!severity) return null;

  const getSeverityColor = () => {
    switch (severity) {
      case 'mild':
        return 'vai-bg-yellow-400';
      case 'moderate':
        return 'vai-bg-orange-500';
      case 'severe':
        return 'vai-bg-red-600';
      case 'normal':
        return isPatientPrintout ? 'vai-bg-green-500' : 'vai-bg-gray-200';
      default:
        return 'vai-bg-gray-200';
    }
  };

  const getTranslatedSeverity = () => {
    switch (severity) {
      case 'mild':
        return t`Mild`;
      case 'moderate':
        return t`Moderate`;
      case 'severe':
        return t`Severe`;
      case 'normal':
        return isPatientPrintout ? t`Healthy` : t`Normal`;
      default:
        return t`Unknown`;
    }
  };

  return (
    <Tooltip open={hideTooltip ? false : undefined}>
      <TooltipTrigger asChild>
        <div
          className={classNames(
            'vai-rounded vai-py-[2px] vai-px-1',
            getSeverityColor(),
          )}
          style={{
            // These styles force the background color to appear when printed
            // rather than stripped entirely, which apparently is common behavior
            // for browsers.
            printColorAdjust: 'exact',
            WebkitPrintColorAdjust: 'exact',
            colorAdjust: 'exact',
          }}
        >
          <p
            className={classNames(
              'vai-font-inter vai-text-xs vai-font-semibold vai-uppercase',
              {
                'vai-text-white':
                  severity !== 'unknown' &&
                  (severity !== 'normal' || isPatientPrintout),
                'vai-text-black':
                  severity === 'unknown' ||
                  (severity === 'normal' && !isPatientPrintout),
              },
            )}
          >
            {getTranslatedSeverity()}
          </p>
        </div>
      </TooltipTrigger>
      <TooltipContent variant="dark" portal className="vai-max-w-xs">
        <p className="vai-text-sm [@media(max-width:310px)]:!vai-text-[12px] vai-leading-relaxed">
          <Trans>
            Represents the most severe bone level reading across all images of
            this quadrant.
          </Trans>
        </p>
      </TooltipContent>
    </Tooltip>
  );
};
